import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('shrt_token'));
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [originalAdmin, setOriginalAdmin] = useState(null);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check for stored admin token on load
  useEffect(() => {
    const storedAdminToken = localStorage.getItem('shrt_admin_token');
    const storedAdminUser = localStorage.getItem('shrt_admin_user');
    if (storedAdminToken && storedAdminUser) {
      setOriginalAdmin({
        token: storedAdminToken,
        user: JSON.parse(storedAdminUser)
      });
      setImpersonating(true);
    }
  }, []);

  // Check if user is authenticated
  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('shrt_token');
    
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('shrt_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email: email.toLowerCase(),
        password
      });
      
      const { token: newToken, member } = response.data;
      
      localStorage.setItem('shrt_token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(member);
      
      return { success: true, user: member };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login fehlgeschlagen';
      return { success: false, error: message };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('shrt_token');
    localStorage.removeItem('shrt_admin_token');
    localStorage.removeItem('shrt_admin_user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setImpersonating(false);
    setOriginalAdmin(null);
  };

  // Impersonate a member (admin only)
  const impersonate = async (memberId) => {
    try {
      // Store current admin credentials before impersonating
      if (user?.is_admin && !impersonating) {
        localStorage.setItem('shrt_admin_token', token);
        localStorage.setItem('shrt_admin_user', JSON.stringify(user));
        setOriginalAdmin({ token, user });
      }

      const response = await axios.post(`${API_URL}/api/admin/impersonate/${memberId}`);
      const { token: newToken, member } = response.data;
      
      localStorage.setItem('shrt_token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(member);
      setImpersonating(true);
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Impersonation fehlgeschlagen';
      return { success: false, error: message };
    }
  };

  // Stop impersonating and return to admin
  const stopImpersonating = () => {
    if (originalAdmin) {
      localStorage.setItem('shrt_token', originalAdmin.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${originalAdmin.token}`;
      setToken(originalAdmin.token);
      setUser(originalAdmin.user);
      setImpersonating(false);
      localStorage.removeItem('shrt_admin_token');
      localStorage.removeItem('shrt_admin_user');
      setOriginalAdmin(null);
    }
  };

  // Request password setup
  const requestPasswordSetup = async (email) => {
    try {
      await axios.post(`${API_URL}/api/auth/request-password-setup`, {
        email: email.toLowerCase()
      });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Anfrage fehlgeschlagen';
      return { success: false, error: message };
    }
  };

  // Verify setup token
  const verifySetupToken = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-token/${token}`);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Token ungültig';
      return { success: false, error: message };
    }
  };

  // Setup password
  const setupPassword = async (setupToken, password) => {
    try {
      await axios.post(`${API_URL}/api/auth/setup-password`, {
        token: setupToken,
        password
      });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Passwort-Einrichtung fehlgeschlagen';
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.is_admin || false,
    impersonating,
    originalAdmin,
    login,
    logout,
    impersonate,
    stopImpersonating,
    requestPasswordSetup,
    verifySetupToken,
    setupPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
